import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Home, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';

interface RouteErrorBoundaryProps {
  children: React.ReactNode;
  routeName?: string;
}

interface RouteErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

export class RouteErrorBoundary extends React.Component<RouteErrorBoundaryProps, RouteErrorBoundaryState> {
  constructor(props: RouteErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (process.env.NODE_ENV === 'development') {
      console.error(`RouteErrorBoundary caught error in ${this.props.routeName || 'route'}:`, error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      return <RouteErrorFallback error={this.state.error} routeName={this.props.routeName} />;
    }

    return this.props.children;
  }
}

function RouteErrorFallback({ error, routeName }: { error?: Error; routeName?: string }) {
  const navigate = useNavigate();

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-4">
      <div className="max-w-lg w-full">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
          <div className="flex items-center justify-center w-16 h-16 mx-auto bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
            <AlertTriangle className="w-8 h-8 text-red-600 dark:text-red-400" aria-hidden="true" />
          </div>
          
          <h2 className="text-2xl font-bold mb-2 text-gray-900 dark:text-gray-100">
            {routeName ? `Error in ${routeName}` : 'Page Error'}
          </h2>
          
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Something went wrong while loading this page. You can try reloading or go back to the dashboard.
          </p>

          {error && process.env.NODE_ENV === 'development' && (
            <div className="bg-gray-100 dark:bg-gray-900 rounded p-4 mb-6 text-left">
              <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">Error Details:</p>
              <p className="text-sm font-mono text-red-600 dark:text-red-400 break-words">
                {error.message}
              </p>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={() => window.location.reload()}
              variant="default"
              className="gap-2"
              aria-label="Reload page"
            >
              <RefreshCw className="w-4 h-4" aria-hidden="true" />
              Reload Page
            </Button>
            <Button
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="gap-2"
              aria-label="Go to dashboard home"
            >
              <Home className="w-4 h-4" aria-hidden="true" />
              Go to Dashboard
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
